import React from 'react';

/**
 * Reusable error state block with SVG icon, styled per KanPlan design system.
 * Replaces plain-text error messages with a proper centred error display.
 *
 * @param {object}  props
 * @param {string}  props.message   - Main error message (required).
 * @param {string}  [props.detail]  - Optional secondary/detail text.
 * @param {'error'|'empty'|'info'} [props.variant='error'] - Visual variant.
 * @param {object}  [props.style]   - Extra wrapper style overrides.
 */
export default function ErrorBlock({ message, detail, variant = 'error', style }) {
  const isError = variant === 'error';
  const isEmpty = variant === 'empty';

  const bgColor = isError ? '#FEF2F2' : isEmpty ? '#F8F9FB' : '#EFF6FF';
  const iconStroke = isError ? '#EF4444' : isEmpty ? '#94A3B8' : '#3B82F6';

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '64px 24px',
        textAlign: 'center',
        ...style,
      }}
    >
      <div
        style={{
          width: 52,
          height: 52,
          borderRadius: '50%',
          background: bgColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: 14,
        }}
      >
        {isError ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
        ) : isEmpty ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round">
            <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={iconStroke} strokeWidth="2" strokeLinecap="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
        )}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--kp-text, #1E293B)', marginBottom: 4 }}>
        {message}
      </div>
      {detail && (
        <div style={{ fontSize: 13, color: 'var(--kp-muted, #64748B)', maxWidth: 400, lineHeight: 1.5 }}>
          {detail}
        </div>
      )}
    </div>
  );
}
